// frontend/src/pages/Projects.tsx

import { useEffect, useState } from 'react';
import { useProjects } from '../hooks/useProjects';
import { projectService } from '../services/project.service';
import { ProjectCard } from '../components/projects/ProjectCard';
import { LastEvaluationCard } from '../components/projects/LastEvaluationCard';
import { PersonalizedProjectGenerator } from '../components/projects/PersonalizedProjectGenerator';
import { SubmissionSummary } from '../types/project.types';
import '../styles/projects.css';

import { getProjectCategory } from '../types/project.types';

const CATEGORIES = [
  { id: 'all', name: 'Tüm Projeler', icon: '🎓' },
  { id: 'math', name: 'Matematik & Mantık', icon: '📐' },
  { id: 'coding', name: 'Kodlama & Yazılım', icon: '🐍' },
  { id: 'science', name: 'Fen & Teknoloji', icon: '🧪' },
  { id: 'english', name: 'İngilizce', icon: '🇬🇧' },
  { id: 'turkish', name: 'Türkçe', icon: '📚' },
];

export function ProjectsPage() {
  const { projects, isLoading, error } = useProjects();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [lastEvaluation, setLastEvaluation] = useState<{
    submission: SubmissionSummary;
    projectTitle: string;
  } | null>(null);

  // En son değerlendirilen projeyi bul
  useEffect(() => {
    if (projects.length === 0) return;
    let cancelled = false;

    async function findLatest() {
      const completedProjects = projects.filter((p) => p.bestScore !== null);
      if (completedProjects.length === 0) return;

      const histories = await Promise.all(
        completedProjects.map(async (p) => {
          try {
            const subs = await projectService.getSubmissionHistory(p.id);
            return subs[0] ? { submission: subs[0], projectTitle: p.title } : null;
          } catch {
            return null;
          }
        })
      );

      if (cancelled) return;
      const valid = histories.filter((h): h is { submission: SubmissionSummary; projectTitle: string } => h !== null);
      if (valid.length === 0) return;

      valid.sort(
        (a, b) => new Date(b.submission.created_at).getTime() - new Date(a.submission.created_at).getTime()
      );
      setLastEvaluation(valid[0]);
    }

    findLatest();
    return () => {
      cancelled = true;
    };
  }, [projects]);

  const filteredProjects = projects.filter((p) => {
    if (activeCategory === 'all') return true;
    const cat = getProjectCategory(p);
    return cat.key === activeCategory;
  });

  return (
    <div className="projects-page">
      <header className="projects-header">
        <h1>Projelerim 🎯</h1>
        <p>Matematik, Fen, Türkçe, İngilizce ve Kodlama derslerinde proje tabanlı interaktif öğrenme görevleri</p>
      </header>

      <PersonalizedProjectGenerator />

      {/* Ders Filtreleme Sekmeleri */}
      <div className="quiz-category-tabs" style={{ marginBottom: 24 }}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={`quiz-category-tab ${activeCategory === cat.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            <span>{cat.icon}</span> {cat.name}
          </button>
        ))}
      </div>

      {isLoading && <div className="courses-loading">Yükleniyor...</div>}
      {error && <div className="courses-error">{error}</div>}

      {!isLoading && !error && (
        <>
          {filteredProjects.length === 0 ? (
            <div className="card" style={{ padding: 32, textAlign: 'center', color: '#64748B' }}>
              <p style={{ fontSize: 16, fontWeight: 600 }}>Bu kategoride henüz yayınlanmış varsayılan proje bulunmuyor.</p>
              <p style={{ fontSize: 14, marginTop: 4 }}>
                Yukarıdaki <strong>"AI ile İstediğin Derste Bana Özel Proje Üret"</strong> kutucuğuna ilgi alanını yazarak anında kendi ders projeni üretebilirsin! 🚀
              </p>
            </div>
          ) : (
            <div className="grid grid-2">
              {filteredProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}
        </>
      )}

      {lastEvaluation && (
        <LastEvaluationCard
          submission={lastEvaluation.submission}
          projectTitle={lastEvaluation.projectTitle}
        />
      )}
    </div>
  );
}
